import { useId } from 'react';
import {
  ArrowRightIcon,
  CheckCircleIcon,
  ClockIcon,
  WarningCircleIcon,
} from '@phosphor-icons/react';
import { Button } from '@voll-intelligence/ui-kit/ui/button';
import { Skeleton } from '@voll-intelligence/ui-kit/ui/skeleton';
import { Spinner } from '@voll-intelligence/ui-kit/ui/spinner';
import { cn } from '@voll-intelligence/ui-kit/utils';
import { useGenUiI18n, type GenUiKey } from '../../../../shared/ui/i18n.js';
import type { SubmitBlocker } from '@voll-intelligence/genui-contract';
import type { CheckoutOutcome, OneClickStage } from '../../core/use_one_click_checkout.js';
import {
  blockersMessage,
  type CheckoutMotiveCopy,
} from '@voll-intelligence/intelligence-ui-kit/checkout';
import { useCheckoutEnv } from '../../core/checkout_env.js';
import { OneClickArrivingButton } from './one_click_arrival.js';

export interface OneClickActionsProps {
  readonly stage: OneClickStage;
  /** Card congelado (replay, challenge resolvido): só o registro sobrevive. */
  readonly readOnly: boolean;
  /** Remontado do histórico: o registro aparece pronto, sem o fade da chegada. */
  readonly replay?: boolean;
  /** Desfecho do pedido, dito pelo host. Um card congelado SEM desfecho diz
   *  por que parou; com desfecho, quem fala é o desfecho. */
  readonly outcome?: CheckoutOutcome | undefined;
  /** Há host para receber o envio — sem ele a tela cheia abre só-leitura. */
  readonly canDispatch: boolean;
  readonly canSubmit: boolean;
  readonly blockers: readonly SubmitBlocker[];
  /** Rótulo do CTA da tela cheia fora do 1click ("Solicitar voo"). */
  readonly reserveKey: GenUiKey;
  /** O nome do motivo na dica do que falta; ausente, o da viagem. */
  /** How the product names the motive in the blockers; absent, the trip's. */
  readonly motive?: CheckoutMotiveCopy | undefined;
  /** A copy do registro no vocabulário do produto («Corrida agendada»). */
  readonly records?: OneClickRecordKeys | undefined;
  readonly onChooseAnother?: (() => void) | undefined;
  readonly onReview: () => void;
  readonly onSubmit: () => void;
  /** O voo do serviço até a viagem, do host (`OneClickPort.arrive`). */
  readonly onArrive?: ((from: DOMRect) => Promise<void>) | undefined;
  readonly onArrived: () => void;
}

/** O par de CTAs. Abaixo da régua de componente do DS (`--breakpoint-sm` no
 *  container `checkout`) ele ocupa a linha inteira, um botão ao lado do outro;
 *  os dois só cabem assim na coluna do celular com o respiro e o corpo menores
 *  e sem a seta (`ARROW`). */
const CTAS =
  'flex items-center gap-3 @max-[theme(--breakpoint-sm)]/checkout:w-full ' +
  '@max-[theme(--breakpoint-sm)]/checkout:*:min-w-0 @max-[theme(--breakpoint-sm)]/checkout:*:flex-1 ' +
  '@max-[theme(--breakpoint-sm)]/checkout:*:px-3 @max-[theme(--breakpoint-sm)]/checkout:*:text-sm';
const ARROW = '@max-[theme(--breakpoint-sm)]/checkout:hidden';
/** «Ver outras opções» e o par de CTAs andam sempre juntos, nos cinco produtos.
 *  No largo, uma linha só à direita do valor («Ver outras opções» · «Revisar
 *  dados» · «Pedir»); quando o dinheiro à esquerda é largo (a diária do hotel
 *  com «pelas N noites») o grupo inteiro desce de linha, nunca só o par. No
 *  estreito o grupo vira a última linha do rodapé (abaixo da dica): o par na
 *  largura toda e «Ver outras opções» logo embaixo, como link à esquerda —
 *  nunca solto ao lado do preço. */
const GROUP =
  'ml-auto flex flex-wrap items-center justify-end gap-x-4 gap-y-3 ' +
  '@max-[theme(--breakpoint-sm)]/checkout:order-last @max-[theme(--breakpoint-sm)]/checkout:basis-full ' +
  '@max-[theme(--breakpoint-sm)]/checkout:flex-col @max-[theme(--breakpoint-sm)]/checkout:items-stretch ' +
  '@max-[theme(--breakpoint-sm)]/checkout:gap-y-2';
/** No estreito, «Ver outras opções» vai para baixo do par (ver `GROUP`) e lê
 *  como link à esquerda: o fantasma de 44 centralizado sozinho parecia texto
 *  solto. O traço tem 32 de altura; o `before` devolve os 44 do alvo de toque. */
const OTHER =
  '@max-[theme(--breakpoint-sm)]/checkout:order-last @max-[theme(--breakpoint-sm)]/checkout:self-start ' +
  '@max-[theme(--breakpoint-sm)]/checkout:relative @max-[theme(--breakpoint-sm)]/checkout:h-8 ' +
  '@max-[theme(--breakpoint-sm)]/checkout:px-0 @max-[theme(--breakpoint-sm)]/checkout:hover:bg-transparent ' +
  '@max-[theme(--breakpoint-sm)]/checkout:underline @max-[theme(--breakpoint-sm)]/checkout:underline-offset-4 ' +
  '@max-[theme(--breakpoint-sm)]/checkout:decoration-muted-foreground/50 ' +
  "@max-[theme(--breakpoint-sm)]/checkout:before:absolute @max-[theme(--breakpoint-sm)]/checkout:before:-inset-y-1.5 @max-[theme(--breakpoint-sm)]/checkout:before:inset-x-0 @max-[theme(--breakpoint-sm)]/checkout:before:content-['']";
/** O registro de espera pelo gestor quando o produto não traz a sua copy. */
const PENDING_APPROVAL_KEYS = {
  full: 'productCheckout.mobility.pendingApproval',
  short: 'productCheckout.mobility.pendingApprovalShort',
} as const satisfies NonNullable<OneClickRecordKeys['pendingApproval']>;

const BRAND =
  'bg-(--brand) text-white hover:bg-(--terracotta-600) disabled:opacity-100 ' +
  'disabled:bg-muted disabled:text-muted-foreground';

/** Os botões enquanto o form chega — o gesto só existe com os dados conferíveis. */
function ActionsSkeleton({ withOther }: { readonly withOther: boolean }) {
  return (
    <div className={GROUP}>
      {withOther && <Skeleton className={`h-11 w-32 ${OTHER}`} />}
      <div className={CTAS}>
        <Skeleton className="h-11 w-40" />
        <Skeleton className="h-11 w-44" />
      </div>
    </div>
  );
}

type RecordStage = Extract<
  OneClickStage,
  { kind: 'done' | 'indeterminate' | 'pending_approval' | 'failed' }
>;

/** A copy do registro que o produto troca: o sucesso no nome dele e a espera
 *  pelo gestor (o texto inteiro no largo, o curto ao lado do total no estreito). */
export interface OneClickRecordKeys {
  readonly done?: GenUiKey;
  readonly pendingApproval?: { readonly full: GenUiKey; readonly short: GenUiKey };
}

const RECORD_KINDS: ReadonlySet<OneClickStage['kind']> = new Set([
  'done',
  'indeterminate',
  'pending_approval',
  'failed',
]);

/** O pedido já tem desfecho a registrar: só o registro sobrevive no fecho. */
function isRecord(stage: OneClickStage): stage is RecordStage {
  return RECORD_KINDS.has(stage.kind);
}

/** A recusa dita pelo turno (#5514): com o pedido novo logo abaixo, ou com o
 *  caminho na resposta. */
const FAILED_KEY = {
  new_checkout: 'productCheckout.oneClick.failedNewCheckout',
  none: 'productCheckout.oneClick.failed',
} as const satisfies Record<Extract<RecordStage, { kind: 'failed' }>['retry'], GenUiKey>;

/** O glifo do registro (#6324, Figma «Registro» do 1-Click): Phosphor 16 no peso
 *  Bold, contorno — o mesmo par dos três desfechos. */
const RECORD_GLYPH = { size: 16, weight: 'bold', 'aria-hidden': true } as const;

/** O registro do desfecho: o ✓ do pedido que entrou; sem resposta do
 *  fornecedor (#5360), o «em análise» neutro — nem sucesso nem a falha que
 *  convida a pedir de novo; e a recusa (#5514), que não convida a pedir neste
 *  card. */
function OneClickRecord({
  stage,
  replay,
  records,
}: {
  readonly stage: RecordStage;
  readonly replay: boolean;
  readonly records: OneClickRecordKeys | undefined;
}) {
  const { t } = useGenUiI18n();
  if (stage.kind === 'indeterminate') {
    return (
      <p
        role="status"
        aria-live="polite"
        className="m-0 flex items-center gap-1.5 text-sm text-muted-foreground"
      >
        <ClockIcon {...RECORD_GLYPH} className="shrink-0" />
        {t('productCheckout.oneClick.indeterminate')}
      </p>
    );
  }
  if (stage.kind === 'pending_approval') {
    // #6699: neutro como o «em análise» — o pedido entrou, falta o gestor. No
    // estreito o curto fica ao lado do total; o inteiro segue para o leitor.
    const keys = records?.pendingApproval ?? PENDING_APPROVAL_KEYS;
    return (
      <p
        role="status"
        aria-live="polite"
        className="m-0 flex items-center gap-1.5 text-sm text-muted-foreground"
      >
        <ClockIcon {...RECORD_GLYPH} className="shrink-0" />
        <span className="@max-[theme(--breakpoint-sm)]/checkout:sr-only">{t(keys.full)}</span>
        <span aria-hidden="true" className="hidden @max-[theme(--breakpoint-sm)]/checkout:inline">
          {t(keys.short)}
        </span>
      </p>
    );
  }
  if (stage.kind === 'failed') {
    return (
      <p
        role="status"
        aria-live="polite"
        className="m-0 flex items-center gap-1.5 text-sm text-destructive"
      >
        <WarningCircleIcon {...RECORD_GLYPH} className="shrink-0" />
        {t(FAILED_KEY[stage.retry])}
      </p>
    );
  }
  return (
    <p
      role="status"
      aria-live="polite"
      // O registro chega depois do pouso na viagem: 300 ms de fade, como no
      // artboard. No replay ele já estava lá.
      className={cn(
        'm-0 flex items-center gap-1.5 text-sm font-medium text-success-strong',
        !replay && 'animate-in fade-in duration-300 motion-reduce:animate-none',
      )}
    >
      <CheckCircleIcon {...RECORD_GLYPH} className="shrink-0 text-success" />
      {t(records?.done ?? 'productCheckout.oneClick.done')}
    </p>
  );
}

/** O turno disse o desfecho. O `unsent` é da casca, não do turno: não conta. */
function answeredByTurn(outcome: CheckoutOutcome | undefined): boolean {
  return outcome !== undefined && outcome.kind !== 'unsent';
}

/** As ações do fecho do card. Pronto: «Ver outras opções» · «Revisar dados» ·
 *  «Pedir com 1-Click» (com a dica do que falta quando trava). Solicitando:
 *  só o botão travado, que encolhe até a bolinha quando o pedido entra.
 *  Solicitado: o registro ✓. Fora do 1click (replay, sem roster, sem host
 *  que receba o envio): o CTA de sempre, que abre a tela cheia. */
export function OneClickActions({
  stage,
  readOnly,
  replay = false,
  outcome,
  canDispatch,
  canSubmit,
  blockers,
  reserveKey,
  motive,
  records,
  onChooseAnother,
  onReview,
  onSubmit,
  onArrive,
  onArrived,
}: OneClickActionsProps) {
  const { t, locale } = useGenUiI18n();
  const { copy } = useCheckoutEnv();
  const hintId = useId();
  if (isRecord(stage)) return <OneClickRecord stage={stage} replay={replay} records={records} />;
  if (stage.kind === 'submitting' && stage.arriving) {
    return (
      <OneClickArrivingButton
        label={t('productCheckout.oneClick.submitting')}
        className={BRAND}
        onArrive={onArrive}
        onArrived={onArrived}
      />
    );
  }
  if (stage.kind === 'submitting') {
    // #6699: «Solicitando» na cor cheia, ocupado — não o travado a meia-luz.
    return (
      <Button size="xl" aria-busy="true" aria-disabled="true" className={BRAND}>
        {t('productCheckout.oneClick.submitting')}
        <Spinner aria-hidden />
      </Button>
    );
  }
  if (readOnly) {
    // O card não carrega a validade da cotação: congelado sem desfecho, a
    // cotação pode ter vencido (challenge de 10 min, sessão do voo de 15) ou
    // um card mais novo tomou o lugar — nos dois casos, o caminho é o agente.
    return answeredByTurn(outcome) ? null : (
      <p className="m-0 ml-auto text-sm text-muted-foreground @max-[theme(--breakpoint-sm)]/checkout:basis-full">
        {t('productCheckout.frozenNotice')}
      </p>
    );
  }
  if (stage.kind === 'loading') return <ActionsSkeleton withOther={Boolean(onChooseAnother)} />;
  const other = onChooseAnother && (
    <Button variant="ghost" size="xl" className={OTHER} onClick={onChooseAnother}>
      {t('productCheckout.otherOptions')}
    </Button>
  );
  if (stage.kind === 'off') {
    return (
      <div className={GROUP}>
        {other}
        <div className={CTAS}>
          <Button size="xl" disabled={!canDispatch} onClick={onReview}>
            {t(reserveKey)}
            <ArrowRightIcon aria-hidden />
          </Button>
        </div>
      </div>
    );
  }
  const hint =
    stage.kind === 'ready' ? blockersMessage(copy, locale, [...blockers], motive) : undefined;
  return (
    <>
      <div className={GROUP}>
        {other}
        <div className={CTAS}>
          <Button variant="outline" size="xl" onClick={onReview}>
            {t('productCheckout.oneClick.review')}
          </Button>
          <Button
            size="xl"
            className={BRAND}
            disabled={stage.kind !== 'ready' || !canSubmit}
            aria-describedby={hint ? hintId : undefined}
            onClick={onSubmit}
          >
            {t('productCheckout.oneClick.submit')}
            <ArrowRightIcon aria-hidden className={ARROW} />
          </Button>
        </div>
      </div>
      {hint && (
        <p
          id={hintId}
          className="m-0 basis-full text-right text-xs font-medium text-warning-strong @max-[theme(--breakpoint-sm)]/checkout:text-center"
        >
          {hint}
        </p>
      )}
    </>
  );
}
